import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Building2, Stethoscope, User, LogIn, BookOpen } from 'lucide-react';
import { useAuth } from '../auth';
import { scrollWindowToTop } from './ScrollToTop';
import { NAV_LEAD_MS, navDirection, setNavDirection } from '../lib/motion';

type Tab = {
  href: string;
  label: string;
  Icon: typeof Home;
  isCenter: boolean;
};

function tabMatches(pathname: string, href: string) {
  if (href === '/') return pathname === '/';
  // Student club is only the club root — not profile / booking / consult subpages.
  if (href === '/student') return pathname === '/student';
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function BottomNav() {
  const { me } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const rowRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const navTimer = useRef<number | null>(null);
  const [optimisticHref, setOptimisticHref] = useState<string | null>(null);
  const [pill, setPill] = useState({ left: 0, top: 0, size: 40, ready: false });

  const profileHref =
    me?.role === 'clinic_staff' ? '/clinic' :
    me?.role === 'doctor'       ? '/doctor' :
    me?.role === 'student'      ? '/student/profile' :
    me                          ? '/student' :
    '/login';

  const isStudent = me?.role === 'student';
  const homeHref = isStudent ? '/student' : '/';

  const tabs: Tab[] = useMemo(() => [
    { href: '/doctors', label: 'Mentor', Icon: Stethoscope, isCenter: false },
    { href: '/clinics', label: 'Internships', Icon: Building2, isCenter: false },
    { href: homeHref, label: isStudent ? 'Club' : 'Home', Icon: Home, isCenter: true },
    { href: '/blogs', label: 'Learn', Icon: BookOpen, isCenter: false },
    {
      href: profileHref,
      label: me ? 'Profile' : 'Log in',
      Icon: me ? User : LogIn,
      isCenter: false,
    },
  ], [me, profileHref, homeHref, isStudent]);

  useEffect(() => {
    setOptimisticHref(null);
  }, [pathname]);

  useEffect(() => () => {
    if (navTimer.current != null) window.clearTimeout(navTimer.current);
  }, []);

  const activeHref =
    optimisticHref ??
    tabs.find(t => tabMatches(pathname, t.href))?.href ??
    null;

  const activeIndex = activeHref == null ? -1 : tabs.findIndex(t => t.href === activeHref);

  const measurePill = useCallback(() => {
    if (me?.role === 'admin' || me?.role === 'clinic_staff' || activeIndex < 0) {
      setPill(p => (p.ready ? { ...p, ready: false } : p));
      return;
    }
    const el = itemRefs.current[activeIndex];
    const row = rowRef.current;
    if (!el || !row) return;

    const iconWrap = el.querySelector('[data-icon-wrap]') as HTMLElement | null;
    const target = iconWrap ?? el;
    const rowBox = row.getBoundingClientRect();
    const box = target.getBoundingClientRect();
    const size = tabs[activeIndex]?.isCenter ? 48 : 40;

    setPill({
      left: box.left - rowBox.left + box.width / 2 - size / 2,
      top: box.top - rowBox.top + box.height / 2 - size / 2,
      size,
      ready: true,
    });
  }, [activeIndex, me?.role, tabs]);

  useLayoutEffect(() => {
    measurePill();
  }, [measurePill]);

  useEffect(() => {
    window.addEventListener('resize', measurePill);
    return () => window.removeEventListener('resize', measurePill);
  }, [measurePill]);

  if (me?.role === 'admin' || me?.role === 'clinic_staff') return null;

  const goTab = (href: string, e: React.MouseEvent) => {
    e.preventDefault();

    if (tabMatches(pathname, href)) {
      scrollWindowToTop();
      return;
    }

    setNavDirection(navDirection(pathname, href));
    setOptimisticHref(href);
    if (navTimer.current != null) window.clearTimeout(navTimer.current);
    navTimer.current = window.setTimeout(() => {
      navigate(href);
      navTimer.current = null;
    }, NAV_LEAD_MS);
  };

  return (
    <nav
      className="site-bottom-nav fixed bottom-0 inset-x-0 z-40 bg-[#0A1E34] border-t border-white/10 md:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div ref={rowRef} className="relative flex items-stretch h-16">
        <span
          aria-hidden
          className="bottom-nav-pill pointer-events-none absolute rounded-full bg-[#E85D26]/22"
          style={{
            width: pill.size,
            height: pill.size,
            transform: `translate3d(${pill.left}px, ${pill.top}px, 0)`,
            opacity: pill.ready ? 1 : 0,
          }}
        />

        {tabs.map(({ href, label, Icon, isCenter }, i) => {
          const active = activeHref === href;
          return (
            <Link
              key={href}
              to={href}
              ref={node => { itemRefs.current[i] = node; }}
              className="relative z-10 flex-1 flex flex-col items-center justify-center gap-0.5 touch-manipulation"
              onClick={e => goTab(href, e)}
            >
              <span
                data-icon-wrap
                className="p-2"
              >
                <Icon
                  className={
                    active
                      ? 'text-[#E85D26] transition-colors duration-[280ms]'
                      : 'text-white/80 transition-colors duration-[280ms]'
                  }
                  size={isCenter ? 22 : 20}
                  strokeWidth={active ? 2.5 : 2}
                />
              </span>
              <span
                className={`text-[10px] font-medium transition-colors duration-[280ms] ${
                  active ? 'text-[#E85D26]' : 'text-white/50'
                }`}
              >
                {label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
